/* The cart, held by Shopify.
 *
 * Only the cart's id is kept here; the lines, the prices and the total are
 * whatever Shopify last said they were, because that is what the checkout will
 * charge. Each line is matched back to the local record by product handle, for
 * the photograph and the name to draw it with.
 *
 * Every mutation is awaited rather than applied optimistically and reconciled.
 * A stepper that moves before the shop has agreed is a stepper that sometimes
 * has to move back, and this cart is small enough that the honest version
 * costs a few hundred milliseconds and no ambiguity.
 *
 * It deliberately touches no DOM. Pinning the body while the drawer is open is
 * the drawer's business (see store/scrollLock.js). */

import { reactive } from 'vue';
import { getProductByHandle } from '@/config/products.js';
import {
  addCartLines,
  createCart,
  fetchCart,
  isConfigured,
  removeCartLines,
  updateCartLines,
} from '@/api/shopify.js';

const STORAGE_KEY = 'amite.cartId';

// Matches the stepper on the product page; a cart line cannot outgrow it.
export const MAX_QUANTITY = 10;

function readStoredId() {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    // Storage blocked: the cart still works, it just will not outlive the tab.
    return null;
  }
}

function storeId(id) {
  try {
    if (id) window.localStorage.setItem(STORAGE_KEY, id);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    // As above — a full or blocked store must not take the cart down with it.
  }
}

function clampQuantity(value) {
  const whole = Math.floor(Number(value));
  if (!Number.isFinite(whole) || whole < 1) return 1;
  return Math.min(whole, MAX_QUANTITY);
}

/* Shopify's line, plus the local half of the product. A handle the site does
   not carry keeps its line rather than being hidden: the checkout would still
   charge for it, and a cart that shows less than it costs is worse than one
   showing a line it cannot illustrate. */
function decorate(line) {
  const product = getProductByHandle(line.handle);
  return {
    ...line,
    product,
    name: product ? product.name : line.handle,
  };
}

export const cartStore = reactive({
  id: null,
  lines: [],
  subtotal: null,
  checkoutUrl: null,
  drawerOpen: false,

  /* One flag for "a request is in flight" and a set of line ids for which line
     it belongs to, so a row can disable its own controls without freezing the
     whole drawer. */
  busy: false,
  busyLines: [],
  error: null,

  get itemCount() {
    return this.lines.reduce((total, line) => total + line.quantity, 0);
  },

  get isEmpty() {
    return this.lines.length === 0;
  },

  /* Shopify keeps a cart for months, so a returning reader usually has one.
     A stale id is not an error — it is an empty cart, and it is forgotten. */
  async restore() {
    if (!isConfigured) return;
    const saved = readStoredId();
    if (!saved) return;

    this.id = saved;
    try {
      const cart = await fetchCart(saved);
      if (cart) this.apply(cart);
      else this.forget();
    } catch (error) {
      this.forget();
    }
  },

  apply(cart) {
    this.id = cart.id;
    this.lines = cart.lines.map(decorate);
    this.subtotal = cart.subtotal;
    this.checkoutUrl = cart.checkoutUrl;
    storeId(cart.id);
  },

  forget() {
    this.id = null;
    this.lines = [];
    this.subtotal = null;
    this.checkoutUrl = null;
    storeId(null);
  },

  async addVariant(variantId, quantity = 1) {
    const lines = [{ merchandiseId: variantId, quantity: clampQuantity(quantity) }];

    return this.run(null, async () => {
      if (!this.id) return createCart(lines);

      try {
        return await addCartLines(this.id, lines);
      } catch (error) {
        /* The one failure worth retrying: a cart id that Shopify has since
           swept up. Anything else would fail the same way a second time, so
           it is left to the caller. */
        this.forget();
        return createCart(lines);
      }
    });
  },

  async setQuantity(lineId, quantity) {
    // Stepping below one is how a line is emptied, so it is how it is removed.
    if (Number(quantity) < 1) return this.removeLine(lineId);
    return this.run(lineId, () => updateCartLines(this.id, [
      { id: lineId, quantity: clampQuantity(quantity) },
    ]));
  },

  async removeLine(lineId) {
    return this.run(lineId, () => removeCartLines(this.id, [lineId]));
  },

  /* The one place that talks to Shopify: it marks the cart busy, applies
     whatever cart comes back, and turns a failure into something the drawer
     can say out loud instead of an unhandled rejection. */
  async run(lineId, action) {
    if (this.busy && !lineId) return false;

    this.busy = true;
    if (lineId) this.busyLines.push(lineId);
    this.error = null;

    try {
      const cart = await action();
      if (cart) this.apply(cart);
      return true;
    } catch (error) {
      this.error = 'Das hat gerade nicht geklappt. Bitte versuch es nochmal.';
      return false;
    } finally {
      this.busy = false;
      const index = this.busyLines.indexOf(lineId);
      if (index !== -1) this.busyLines.splice(index, 1);
    }
  },

  isLineBusy(lineId) {
    return this.busyLines.includes(lineId);
  },

  openDrawer() {
    this.drawerOpen = true;
  },

  closeDrawer() {
    this.drawerOpen = false;
    this.error = null;
  },
});
